import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadialBarChart, RadialBar,
} from "recharts";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ═══════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════ */
const dashboardStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  :root {
    --navy-900: #0B1F40;
    --navy-800: #0f2a55;
    --navy-700: #132f60;
    --accent-gold: #F4B942;
    --accent-gold-light: #f9d07a;
    --accent-cyan: #38BDF8;
    --accent-cyan-light: #0ea5e9;
    --accent-emerald: #10B981;
    --accent-rose: #F43F5E;
    --accent-violet: #8B5CF6;
    --glass-border: rgba(15, 42, 85, 0.12);
    --text-primary: #0B1F40;
    --text-secondary: #1a3d7a;
    --text-muted: #5a7ba8;
    --shadow-card: 0 4px 24px rgba(11, 31, 64, 0.08);
    --radius-lg: 20px;
    --radius-md: 14px;
    --radius-sm: 10px;
  }

  .dash-root {
    font-family: 'Sora', sans-serif;
    background: #f5f8ff;
    min-height: 100vh;
    padding: 28px 32px 60px;
    position: relative;
    overflow-x: hidden;
  }

  .dash-root::before {
    content: '';
    position: fixed;
    top: -200px; right: -200px;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(56,189,248,0.07) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .dash-root::after {
    content: '';
    position: fixed;
    bottom: -200px; left: -100px;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(244,185,66,0.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .dash-content { position: relative; z-index: 1; }

  /* ── Header ── */
  .dash-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--glass-border);
    flex-wrap: wrap;
    gap: 16px;
  }

  .dash-title-wrap { display: flex; align-items: center; gap: 14px; }

  .dash-title-icon {
    width: 46px; height: 46px;
    background: linear-gradient(135deg, var(--accent-cyan), #0ea5e9);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; color: #fff;
    box-shadow: 0 4px 20px rgba(56,189,248,0.35);
  }

  .dash-title-text {
    font-size: 26px; font-weight: 700;
    color: var(--text-primary);
    letter-spacing: -0.5px; margin: 0;
  }

  .dash-title-sub {
    font-size: 11px; color: var(--text-muted);
    font-weight: 500; letter-spacing: 1px; margin-top: 2px;
    text-transform: uppercase;
  }

  .dash-header-controls { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }

  .dash-select-wrap { display: flex; align-items: center; gap: 10px; }

  .dash-select-label { font-size: 13px; font-weight: 600; color: var(--text-secondary); white-space: nowrap; }

  .dash-select {
    background: #fff;
    border: 1.5px solid var(--glass-border);
    color: var(--text-primary);
    font-family: 'Sora', sans-serif;
    font-size: 13px; font-weight: 500;
    padding: 9px 34px 9px 14px;
    border-radius: var(--radius-sm);
    cursor: pointer; outline: none;
    transition: all 0.25s ease;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2338BDF8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    min-width: 180px;
    box-shadow: 0 1px 4px rgba(11,31,64,0.06);
  }

  .dash-select:hover, .dash-select:focus {
    border-color: var(--accent-cyan);
    box-shadow: 0 0 0 3px rgba(56,189,248,0.15);
  }

  /* ── Stat Cards ── */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 28px;
  }

  .stat-card {
    background: #fff;
    position: relative;
    border-radius: var(--radius-lg);
    padding: 24px;
    overflow: hidden;
    border: 1.5px solid var(--glass-border);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: pointer;
    animation: fadeInUp 0.5s ease forwards;
  }

  .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(11,31,64,0.12);
  }

  .stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }

  .stat-card-admissions { background: linear-gradient(145deg, rgba(56,189,248,0.06), #fff); }
  .stat-card-admissions::before { background: linear-gradient(90deg, var(--accent-cyan), var(--accent-cyan-light)); }

  .stat-card-header {
    display: flex; justify-content: space-between;
    align-items: flex-start; margin-bottom: 16px;
  }

  .stat-card-title {
    font-size: 11px; font-weight: 700;
    color: var(--text-muted);
    letter-spacing: 1px; text-transform: uppercase;
  }

  .stat-card-icon {
    width: 38px; height: 38px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
  }

  .icon-cyan { background: rgba(56,189,248,0.15); color: #0284c7; }

  .stat-total-value {
    font-size: 34px; font-weight: 800;
    color: var(--text-primary);
    letter-spacing: -1px; line-height: 1;
    margin-bottom: 4px;
    font-family: 'JetBrains Mono', monospace;
  }

  .stat-total-label {
    font-size: 11px; color: var(--text-muted);
    font-weight: 500; text-transform: uppercase;
    letter-spacing: 0.6px; display: block; margin-bottom: 4px;
  }

  .stat-grid-bottom {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 4px; margin-top: 16px; padding-top: 16px;
    border-top: 1px solid var(--glass-border);
  }

  .stat-mini {
    text-align: center; padding: 8px 4px;
    border-radius: 8px; cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;
    position: relative;
  }

  .stat-mini:hover {
    background: rgba(56,189,248,0.08);
    transform: translateY(-2px);
  }

  .stat-mini:hover .stat-mini-value { color: #0284c7; }

  .stat-mini-label {
    font-size: 10px; color: var(--text-muted);
    font-weight: 600; display: block;
    margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.4px;
  }

  .stat-mini-value {
    font-size: 16px; font-weight: 700;
    color: var(--text-primary);
    font-family: 'JetBrains Mono', monospace;
    transition: color 0.2s ease;
  }

  .stat-mini-hint {
    font-size: 9px; color: var(--accent-cyan);
    font-weight: 500; display: block; margin-top: 2px;
    opacity: 0; transition: opacity 0.2s ease;
  }

  .stat-mini:hover .stat-mini-hint { opacity: 1; }

  /* ── Section Cards ── */
  .section-card {
    background: #fff;
    border: 1.5px solid var(--glass-border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-card);
    margin-bottom: 24px;
  }

  .section-card-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 20px 28px 18px;
    border-bottom: 1.5px solid var(--glass-border);
    background: linear-gradient(90deg, rgba(56,189,248,0.03), transparent);
    flex-wrap: wrap; gap: 12px;
  }

  .section-title-wrap { display: flex; align-items: center; gap: 12px; }

  .section-icon {
    width: 38px; height: 38px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
    background: linear-gradient(135deg, rgba(56,189,248,0.15), rgba(56,189,248,0.08));
    color: #0284c7;
    border: 1px solid rgba(56,189,248,0.2);
  }

  .section-title {
    font-size: 16px; font-weight: 700;
    color: var(--text-primary); margin: 0; letter-spacing: -0.3px;
  }

  .section-body { padding: 24px 28px; }

  /* ── Attendance Toggle ── */
  .att-toggle-group {
    display: flex; align-items: center; gap: 4px;
    background: rgba(15,42,85,0.04);
    padding: 4px; border-radius: 10px;
    border: 1.5px solid var(--glass-border);
  }

  .att-toggle-btn {
    padding: 6px 16px; font-size: 12px; font-weight: 600;
    border: none; background: transparent; cursor: pointer;
    border-radius: 7px; color: var(--text-muted);
    transition: all 0.2s; font-family: 'Sora', sans-serif;
    white-space: nowrap;
  }

  .att-toggle-btn.active {
    background: linear-gradient(135deg, var(--accent-cyan), #0ea5e9);
    color: #fff; box-shadow: 0 2px 12px rgba(56,189,248,0.4);
  }

  .att-toggle-btn:hover:not(.active) {
    background: rgba(56,189,248,0.08); color: var(--text-primary);
  }

  /* ── Loading ── */
  .dashboard-loading-container {
    display: flex; justify-content: center; align-items: center;
    min-height: 400px; width: 100%;
    background: rgba(255,255,255,0.8); border-radius: var(--radius-lg); margin-bottom: 24px;
  }

  .attend-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 24px;
}

.freq-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(15, 42, 85, 0.04);
  padding: 5px;
  border-radius: 10px;
  border: 1.5px solid var(--glass-border);
}
.freq-label-text { font-size: 12px; color: var(--text-muted); padding: 0 8px 0 6px; font-weight: 500; }
.freq-btn { position: relative; display: inline-block; }
.freq-btn input[type="radio"] { position: absolute; opacity: 0; width: 0; height: 0; }
.freq-btn label {
  display: block; padding: 6px 16px; font-size: 12px; font-weight: 600;
  color: var(--text-muted); cursor: pointer; border-radius: 7px;
  transition: all 0.2s ease; white-space: nowrap;
}
.freq-btn input[type="radio"]:checked + label {
  background: linear-gradient(135deg, var(--accent-cyan), #0ea5e9);
  color: #fff; box-shadow: 0 2px 12px rgba(56,189,248,0.4);
}
.freq-btn label:hover { color: var(--text-primary); background: rgba(15, 42, 85, 0.08); }

.attend-scroll-wrap { overflow-x: auto; padding-bottom: 8px; }
.attend-scroll-wrap::-webkit-scrollbar { height: 6px; }
.attend-scroll-wrap::-webkit-scrollbar-track { background: rgba(15,42,85,0.05); border-radius: 3px; }
.attend-scroll-wrap::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.4); border-radius: 3px; }
.attend-scroll-wrap::-webkit-scrollbar-thumb:hover { background: rgba(56,189,248,0.6); }

  .custom-spinner {
    width: 44px; height: 44px;
    border: 3px solid rgba(56,189,248,0.15);
    border-left-color: var(--accent-cyan);
    border-radius: 50%; animation: spin 0.9s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .stat-card:nth-child(1) { animation-delay: 0.05s; }
  .stat-card:nth-child(2) { animation-delay: 0.10s; }

  /* ── Detail Modal ── */
  .detail-modal-overlay {
    position: fixed; inset: 0; z-index: 1200;
    background: rgba(11,31,64,0.52); backdrop-filter: blur(5px);
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: overlayIn 0.2s ease;
  }
  @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

  .detail-modal {
    background: #fff; border-radius: 22px;
    width: 100%; max-width: 900px; max-height: 85vh;
    display: flex; flex-direction: column;
    box-shadow: 0 32px 80px rgba(11,31,64,0.22);
    border: 1.5px solid var(--glass-border);
    animation: modalIn 0.25s cubic-bezier(0.34,1.56,0.64,1);
    overflow: hidden;
  }
  @keyframes modalIn {
    from { opacity: 0; transform: scale(0.93) translateY(16px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  .detail-modal-header {
    padding: 20px 28px;
    border-bottom: 1.5px solid var(--glass-border);
    display: flex; justify-content: space-between; align-items: center;
    background: linear-gradient(90deg, rgba(56,189,248,0.05), transparent);
    flex-shrink: 0;
  }

  .detail-modal-title {
    font-size: 17px; font-weight: 700;
    color: var(--text-primary); margin: 0;
  }

  .detail-modal-subtitle {
    font-size: 12px; color: var(--text-muted); margin-top: 3px;
  }

  .detail-modal-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
  }

  .badge-cyan    { background: rgba(56,189,248,0.12);  color: #0284c7; }
  .badge-emerald { background: rgba(16,185,129,0.12);  color: #059669; }

  .modal-close-btn {
    width: 34px; height: 34px; border-radius: 9px;
    background: rgba(244,63,94,0.1); border: none; cursor: pointer;
    font-size: 14px; color: #e11d48;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s; flex-shrink: 0;
  }
  .modal-close-btn:hover { background: rgba(244,63,94,0.2); }

  .detail-modal-filter-bar {
    display: flex; align-items: center; gap: 8px;
    padding: 14px 28px;
    border-bottom: 1px solid var(--glass-border);
    background: rgba(248,250,252,0.8);
    flex-wrap: wrap; flex-shrink: 0;
  }

  .filter-chip {
    padding: 6px 16px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
    border: 1.5px solid var(--glass-border);
    background: #fff; color: var(--text-secondary);
    cursor: pointer; transition: all 0.2s ease;
    font-family: 'Sora', sans-serif;
  }
  .filter-chip.active {
    background: linear-gradient(135deg, #38BDF8, #0ea5e9);
    border-color: transparent; color: #fff;
    box-shadow: 0 2px 10px rgba(56,189,248,0.35);
  }
  .filter-chip:hover:not(.active) { border-color: #38BDF8; color: #0284c7; }

  .detail-modal-body { overflow-y: auto; flex: 1; padding: 20px 28px; }
  .detail-modal-body::-webkit-scrollbar { width: 5px; }
  .detail-modal-body::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.35); border-radius: 3px; }

  .detail-modal-footer {
    padding: 14px 28px;
    border-top: 1.5px solid var(--glass-border);
    display: flex; justify-content: space-between; align-items: center;
    flex-shrink: 0; flex-wrap: wrap; gap: 10px;
  }

  .modal-page-info { font-size: 12px; color: var(--text-muted); font-weight: 500; }

  .modal-page-btns { display: flex; gap: 4px; }

  .modal-page-btn {
    padding: 5px 12px; font-size: 12px;
    font-family: 'Sora', sans-serif; font-weight: 600;
    border: 1.5px solid var(--glass-border);
    border-radius: 7px; background: #fff; color: var(--text-secondary);
    cursor: pointer; transition: all 0.2s ease;
  }
  .modal-page-btn.active { background: #38BDF8; border-color: #38BDF8; color: #fff; }
  .modal-page-btn:hover:not(.active):not(:disabled) { border-color: #38BDF8; color: #0284c7; }
  .modal-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Modal Table ── */
  .modal-table-wrap {
    border-radius: 12px; border: 1.5px solid var(--glass-border); overflow: auto;
  }
  .modal-table-wrap::-webkit-scrollbar { width: 5px; height: 5px; }
  .modal-table-wrap::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.35); border-radius: 3px; }

  .modal-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
    table-layout: auto; min-width: 500px;
  }
  .modal-table thead { position: sticky; top: 0; z-index: 2; }
  .modal-table thead tr { background: linear-gradient(90deg, #f0f7ff, #e8f4fd); }
  .modal-table thead th {
    padding: 12px 16px; font-size: 11px; font-weight: 700;
    color: #0284c7; text-transform: uppercase; letter-spacing: 0.7px;
    white-space: nowrap; border-bottom: 2px solid #38BDF8; text-align: left;
  }
  .modal-table tbody tr { border-bottom: 1px solid rgba(15,42,85,0.07); transition: background 0.15s; }
  .modal-table tbody tr:hover { background: rgba(56,189,248,0.05); }
  .modal-table tbody tr:nth-child(even) { background: rgba(15,42,85,0.015); }
  .modal-table tbody tr:nth-child(even):hover { background: rgba(56,189,248,0.05); }
  .modal-table tbody td { padding: 11px 16px; color: var(--text-secondary); white-space: nowrap; }
  .modal-table tbody td:first-child { color: #0284c7; font-weight: 600; font-family: 'JetBrains Mono', monospace; }

  .modal-empty {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 56px 24px; gap: 12px; color: var(--text-muted);
  }
  .modal-empty-icon { font-size: 40px; opacity: 0.4; }
  .modal-empty-text { font-size: 13px; font-weight: 500; }

  .status-pill {
    display: inline-flex; align-items: center;
    padding: 3px 10px; border-radius: 999px;
    font-size: 11px; font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
  }
  .status-present { background: rgba(16,185,129,0.12); color: #059669; }
  .status-absent  { background: rgba(244,63,94,0.12);  color: #e11d48; }
  .status-weekoff { background: rgba(244,185,66,0.12); color: #b45309; }

  /* ── Branch-wise Section ── */
  .bws-tabs {
    display: flex; gap: 4px;
    background: rgba(15,42,85,0.04); padding: 5px;
    border-radius: 12px; border: 1.5px solid var(--glass-border);
    width: fit-content; flex-wrap: wrap; margin-bottom: 0;
  }
  .bws-tab {
    padding: 7px 18px; font-size: 12px; font-weight: 600;
    color: var(--text-muted); border-radius: 8px;
    cursor: pointer; border: none; background: transparent;
    transition: all 0.2s ease; white-space: nowrap;
    font-family: 'Sora', sans-serif;
  }
  .bws-tab.active {
    background: linear-gradient(135deg, #38BDF8, #0ea5e9);
    color: #fff; box-shadow: 0 2px 12px rgba(56,189,248,0.4);
  }
  .bws-tab:hover:not(.active) { background: rgba(56,189,248,0.08); color: var(--text-primary); }

  .bws-table-wrap {
    border-radius: 12px; border: 1.5px solid var(--glass-border);
    overflow: auto; max-height: 420px;
  }
  .bws-table-wrap::-webkit-scrollbar { width: 5px; height: 5px; }
  .bws-table-wrap::-webkit-scrollbar-thumb { background: rgba(56,189,248,0.35); border-radius: 3px; }

  .bws-table {
    width: 100%; border-collapse: collapse; font-size: 12.5px;
    table-layout: auto; min-width: 600px;
  }
  .bws-table thead { position: sticky; top: 0; z-index: 3; }
  .bws-table thead tr { background: linear-gradient(90deg, #f0f7ff, #e8f4fd); }
  .bws-table thead th {
    padding: 12px 14px; font-size: 10.5px; font-weight: 700;
    color: #0284c7; text-transform: uppercase; letter-spacing: 0.7px;
    white-space: nowrap; border-bottom: 2px solid #38BDF8; text-align: left;
  }
  .bws-table thead th.bws-center { text-align: center; }
  .bws-table tbody tr { border-bottom: 1px solid rgba(15,42,85,0.07); transition: background 0.15s; }
  .bws-table tbody tr:hover { background: rgba(56,189,248,0.05); }
  .bws-table tbody tr:nth-child(even) { background: rgba(15,42,85,0.015); }
  .bws-table tbody tr:nth-child(even):hover { background: rgba(56,189,248,0.05); }
  .bws-table tbody td { padding: 10px 14px; color: var(--text-secondary); white-space: nowrap; }
  .bws-table tbody td.bws-center { text-align: center; }
  .bws-table tfoot tr { background: linear-gradient(90deg, #f0f7ff, #e8f4fd); border-top: 2px solid #38BDF8; }
  .bws-table tfoot td { padding: 10px 14px; font-weight: 700; font-size: 12px; color: #0B1F40; }

  .branch-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; display: inline-block; }

  .bws-num-pill {
    display: inline-flex; align-items: center; justify-content: center;
    padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 700;
    font-family: 'JetBrains Mono', monospace; min-width: 44px;
  }
  .bws-num-pill.p-total  { background: rgba(56,189,248,0.12); color: #0284c7; }
  .bws-num-pill.p-month  { background: rgba(139,92,246,0.12); color: #7c3aed; }
  .bws-num-pill.p-week   { background: rgba(244,185,66,0.12); color: #b45309; }
  .bws-num-pill.p-today  { background: rgba(16,185,129,0.12); color: #059669; }
  .bws-num-pill.p-zero   { background: rgba(15,42,85,0.05); color: #94a3b8; }

  .bws-pct-bar-wrap { display: flex; align-items: center; gap: 8px; min-width: 110px; }
  .bws-pct-bar-track { flex: 1; height: 6px; background: rgba(15,42,85,0.08); border-radius: 3px; overflow: hidden; }
  .bws-pct-bar-fill { height: 100%; border-radius: 3px; transition: width 0.6s ease; }
  .bws-pct-label { font-size: 11px; font-family: 'JetBrains Mono', monospace; font-weight: 600; color: var(--text-secondary); min-width: 38px; text-align: right; }

  .bws-pagination { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; flex-wrap: wrap; gap: 10px; }
  .bws-page-info { font-size: 12px; color: var(--text-muted); font-weight: 500; }
  .bws-page-btns { display: flex; align-items: center; gap: 4px; }
  .bws-page-btn {
    padding: 5px 12px; font-size: 12px; font-family: 'Sora', sans-serif; font-weight: 600;
    border: 1.5px solid var(--glass-border); border-radius: 7px; background: #fff;
    color: var(--text-secondary); cursor: pointer; transition: all 0.2s;
  }
  .bws-page-btn.active { background: #38BDF8; border-color: #38BDF8; color: #fff; box-shadow: 0 2px 8px rgba(56,189,248,0.35); }
  .bws-page-btn:hover:not(.active):not(:disabled) { border-color: #38BDF8; color: #0284c7; }
  .bws-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .bws-search {
    padding: 8px 14px; font-size: 12px; font-family: 'Sora', sans-serif;
    border: 1.5px solid var(--glass-border); border-radius: 8px;
    color: var(--text-primary); outline: none; transition: all 0.2s;
    background: #fff; min-width: 200px;
  }
  .bws-search:focus { border-color: #38BDF8; box-shadow: 0 0 0 3px rgba(56,189,248,0.15); }

  .bws-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; color: var(--text-muted); gap: 12px; }
  .bws-empty-icon { font-size: 36px; opacity: 0.4; }
  .bws-empty-text { font-size: 13px; font-weight: 500; }

  .bws-legend { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
  .bws-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--text-muted); font-weight: 500; }
  .bws-legend-dot { width: 8px; height: 8px; border-radius: 50%; }

  /* ── Dual Pie / Snapshot ── */
  .dual-pie-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start;
  }
  .single-pie-block { display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .single-pie-title {
    font-size: 13px; font-weight: 700; color: var(--text-primary);
    display: flex; align-items: center; gap: 8px;
  }
  .pie-mini-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; width: 100%; }
  .pie-mini-box {
    padding: 12px 8px; border-radius: 12px; border: 1.5px solid var(--glass-border);
    text-align: center;
  }
  .pie-mini-val { font-size: 20px; font-weight: 800; font-family: 'JetBrains Mono', monospace; line-height: 1; margin-bottom: 4px; }
  .pie-mini-lbl { font-size: 10px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }

  /* ── Bar chart period cards ── */
  .period-summary-row {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px;
  }
  .period-card {
    padding: 14px 16px; border-radius: 12px; border: 1.5px solid var(--glass-border);
    background: #fff; cursor: pointer; transition: all 0.2s;
    display: flex; flex-direction: column; gap: 6px;
  }
  .period-card:hover, .period-card.active {
    border-color: #38BDF8; background: rgba(56,189,248,0.04);
    box-shadow: 0 4px 16px rgba(56,189,248,0.12);
  }
  .period-card-label {
    font-size: 10px; font-weight: 700; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: 0.7px;
  }
  .period-card-nums { display: flex; align-items: baseline; gap: 8px; }
  .period-card-val {
    font-size: 22px; font-weight: 800; font-family: 'JetBrains Mono', monospace;
    color: var(--text-primary); line-height: 1;
  }
  .period-card-sub { font-size: 11px; color: var(--text-muted); }

  /* ── Chart empty state ── */
  .chart-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 12px; padding: 48px 24px;
    color: var(--text-muted);
  }
  .chart-empty-icon { font-size: 42px; opacity: 0.3; }
  .chart-empty-text { font-size: 13px; font-weight: 500; }

  @media (max-width: 900px) {
    .dual-pie-row { grid-template-columns: 1fr; }
    .period-summary-row { grid-template-columns: repeat(2,1fr); }
  }
  @media (max-width: 700px) {
    .dash-root { padding: 16px 14px 40px; }
    .dash-header { flex-direction: column; }
    .period-summary-row { grid-template-columns: 1fr 1fr; }
  }
`;

/* ═══════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════ */
const BRANCH_COLORS = ["#38BDF8","#8B5CF6","#10B981","#F4B942","#F43F5E","#06B6D4","#A78BFA","#34D399","#FBBF24","#FB7185"];
const BWS_PAGE_SIZE  = 8;
const MODAL_PAGE_SIZE = 15;

const BWS_TABS = [
  { key: "studentAdmission",   label: "🎓 Admissions" },
  { key: "studentAttendance",  label: "📅 Student Attendance" },
  { key: "employeeAttendance", label: "👤 Employee Attendance" },
];

// Periods for the attendance bar-chart switcher
const ATT_PERIODS = [
  { key: "total",  label: "Overall",    icon: "📊" },
  { key: "month",  label: "This Month", icon: "📆" },
  { key: "week",   label: "This Week",  icon: "🗓️" },
  { key: "today",  label: "Today",      icon: "☀️" },
];

/* ═══════════════════════════════════════════════════════
   SMALL HELPERS
═══════════════════════════════════════════════════════ */
const BwsPill = ({ value, type = "total" }) => {
  const cls = Number(value) === 0 ? "p-zero" : `p-${type}`;
  return <span className={`bws-num-pill ${cls}`}>{value ?? 0}</span>;
};

const BwsPctBar = ({ pct = 0, color = "#38BDF8" }) => (
  <div className="bws-pct-bar-wrap">
    <div className="bws-pct-bar-track">
      <div className="bws-pct-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
    </div>
    <span className="bws-pct-label">{(pct || 0).toFixed(1)}%</span>
  </div>
);

const StatusPill = ({ status }) => {
  const map = { 1: ["Present","status-present"], 0: ["Absent","status-absent"], 2: ["Week Off","status-weekoff"] };
  const [label, cls] = map[status] ?? ["—",""];
  return <span className={`status-pill ${cls}`}>{label}</span>;
};

/* ═══════════════════════════════════════════════════════
   CUSTOM TOOLTIP for recharts
═══════════════════════════════════════════════════════ */
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1.5px solid rgba(15,42,85,0.12)",
      borderRadius: 12, padding: "12px 16px", fontFamily: "Sora, sans-serif",
      boxShadow: "0 8px 32px rgba(11,31,64,0.12)", minWidth: 180,
    }}>
      <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#0B1F40" }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: p.fill, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: "#5a7ba8", flex: 1 }}>{p.name}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0B1F40", fontFamily: "JetBrains Mono, monospace" }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   DAY ATTENDANCE DETAIL MODAL — reuses existing modal CSS
═══════════════════════════════════════════════════════ */
const DayAttendanceModal = ({ open, loading, title, records, onClose }) => {
  if (!open) return null;
  return (
    <div className="detail-modal-overlay" onClick={onClose}>
      <div className="detail-modal" onClick={e => e.stopPropagation()}>
        <div className="detail-modal-header">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 22 }}>📅</span>
              <h4 className="detail-modal-title">{title}</h4>
              <span className="detail-modal-badge badge-cyan">{records.length} records</span>
            </div>
            <p className="detail-modal-subtitle">Student attendance for the selected day</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="detail-modal-body">
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 56 }}>
              <div className="custom-spinner" />
            </div>
          ) : records.length === 0 ? (
            <div className="modal-empty">
              <span className="modal-empty-icon">📅</span>
              <span className="modal-empty-text">No records found for this day</span>
            </div>
          ) : (
            <div className="modal-table-wrap">
              <table className="modal-table">
                <thead>
                  <tr>
                    <th>#</th><th>Adm No.</th><th>Name</th>
                    <th>Class</th><th>Section</th><th>Date</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{r.admissionNo || "—"}</td>
                      <td><strong style={{ color: "#0B1F40" }}>{r.name || "—"}</strong></td>
                      <td>{r.className || "—"}</td>
                      <td>{r.sectionName || "—"}</td>
                      <td style={{ fontFamily: "JetBrains Mono", fontSize: 11 }}>
                        {r.attendanceDate ? new Date(r.attendanceDate).toLocaleDateString() : "—"}
                      </td>
                      <td><StatusPill status={r.attendanceStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="detail-modal-footer">
          <span className="modal-page-info">{records.length} record(s)</span>
          <button className="modal-page-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   DETAIL MODAL
═══════════════════════════════════════════════════════ */
const DetailModal = ({ config, onClose, selectedSession, branchId }) => {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filterType, setFilterType] = useState(config.defaultFilter || "Today");
  const [page, setPage]         = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.ceil(totalCount / MODAL_PAGE_SIZE) || 1;

  const fetchData = useCallback(async (ft, pg) => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem("token");
      const headers = { "Content-Type": "application/json", Authorization: token };
      let url = "", options = { method: "POST", headers };

      if (config.type === "admissions") {
        url = `${apiUrl}/DashboardTotalNumbers/Dashboard_StudentAdmission_Details?SessionId=${selectedSession}&BranchIds=${branchId}&FilterType=${ft}&PageNumber=${pg}&PageSize=${MODAL_PAGE_SIZE}`;
      } else if (config.type === "attendance") {
        url = `${apiUrl}/DashboardTotalNumbers/GetAttendanceDetails`;
        options.body = JSON.stringify({
          moduleType: config.moduleType || "Student",
          branchId: parseInt(branchId),
          sessionId: parseInt(selectedSession),
          type: config.attendanceType || "",
          filterType: ft,
          pageNumber: pg,
          pageSize: MODAL_PAGE_SIZE,
        });
      }

      const res  = await fetch(url, options);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const rows = json.data || json.Data || [];
      const count = json.count ?? json.Count ?? json.recordsReturned ?? json.RecordsReturned ?? rows.length;
      setData(rows);
      setTotalCount(typeof count === "number" ? count : rows.length);
    } catch (e) {
      console.error("Detail modal fetch error:", e);
      setData([]);
    } finally { setLoading(false); }
  }, [config, selectedSession, branchId]);

  useEffect(() => { fetchData(filterType, page); }, [filterType, page]);

  const filterOptions = ["Today","Week","Month","Total"];
  const badgeClass = config.type === "attendance" ? "badge-emerald" : "badge-cyan";

  const renderAdmissions = () => (
    <table className="modal-table">
      <thead><tr>
        <th>#</th><th>Admission No.</th><th>Student Name</th>
        <th>Class</th><th>Section</th><th>Roll No.</th><th>Branch</th><th>Date</th>
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i}>
            <td>{(page-1)*MODAL_PAGE_SIZE+i+1}</td>
            <td>{r.admissionNo||r.AdmissionNo||"—"}</td>
            <td><strong style={{color:"#0B1F40"}}>{r.studentName||r.StudentName||"—"}</strong></td>
            <td>{r.className||r.ClassName||"—"}</td>
            <td>{r.sectionName||r.SectionName||"—"}</td>
            <td>{r.rollNo||r.RollNo||"—"}</td>
            <td>{r.branchName||r.BranchName||"—"}</td>
            <td style={{fontFamily:"JetBrains Mono",fontSize:11}}>
              {r.createdOn||r.CreatedOn ? new Date(r.createdOn||r.CreatedOn).toLocaleDateString() : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderAttendance = () => (
    <table className="modal-table">
      <thead><tr>
        <th>#</th><th>Adm / Emp No.</th><th>Name</th>
        <th>Class</th><th>Section</th><th>Date</th><th>Status</th>
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i}>
            <td>{(page-1)*MODAL_PAGE_SIZE+i+1}</td>
            <td>{r.admissionNo||r.AdmissionNo||r.employeeCode||r.EmployeeCode||"—"}</td>
            <td><strong style={{color:"#0B1F40"}}>{r.name||r.Name||"—"}</strong></td>
            <td>{r.className||r.ClassName||"—"}</td>
            <td>{r.sectionName||r.SectionName||"—"}</td>
            <td style={{fontFamily:"JetBrains Mono",fontSize:11}}>
              {r.attendanceDate||r.AttendanceDate ? new Date(r.attendanceDate||r.AttendanceDate).toLocaleDateString() : "—"}
            </td>
            <td><StatusPill status={r.attendanceStatus??r.AttendanceStatus} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const tableMap = { admissions: renderAdmissions, attendance: renderAttendance };

  return (
    <div className="detail-modal-overlay" onClick={onClose}>
      <div className="detail-modal" onClick={e => e.stopPropagation()}>
        <div className="detail-modal-header">
          <div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <span style={{fontSize:22}}>{config.icon}</span>
              <h4 className="detail-modal-title">{config.title}</h4>
              <span className={`detail-modal-badge ${badgeClass}`}>{totalCount} records</span>
            </div>
            <p className="detail-modal-subtitle">{config.subtitle}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="detail-modal-filter-bar">
          <span style={{fontSize:12,fontWeight:600,color:"var(--text-muted)",marginRight:4}}>Period:</span>
          {filterOptions.map(f => (
            <button key={f} className={`filter-chip ${filterType===f?"active":""}`}
              onClick={() => { setFilterType(f); setPage(1); }}>{f}</button>
          ))}
        </div>

        <div className="detail-modal-body">
          {loading ? (
            <div style={{display:"flex",justifyContent:"center",padding:56}}>
              <div className="custom-spinner" />
            </div>
          ) : data.length === 0 ? (
            <div className="modal-empty">
              <span className="modal-empty-icon">{config.icon}</span>
              <span className="modal-empty-text">No records found for this period</span>
            </div>
          ) : (
            <div className="modal-table-wrap">
              {(tableMap[config.type]||(() => null))()}
            </div>
          )}
        </div>

        <div className="detail-modal-footer">
          <span className="modal-page-info">Page {page} of {totalPages} · {totalCount} total records</span>
          <div className="modal-page-btns">
            <button className="modal-page-btn" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹ Prev</button>
            {Array.from({length:Math.min(totalPages,7)},(_,i) => {
              const p = totalPages<=7 ? i+1 : page<=4 ? i+1 : page>=totalPages-3 ? totalPages-6+i : page-3+i;
              return <button key={p} className={`modal-page-btn ${p===page?"active":""}`} onClick={()=>setPage(p)}>{p}</button>;
            })}
            <button className="modal-page-btn" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next ›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   BRANCH-WISE SECTION
═══════════════════════════════════════════════════════ */
const BranchWiseSection = ({ branches = [], selectedSession, isMaster }) => {
  const [activeTab, setActiveTab] = useState("studentAdmission");
  const [admissionSearch, setAdmissionSearch] = useState("");
  const [page, setPage] = useState(1);
  const [localData, setLocalData] = useState({ studentAdmission: [], loginDetail: [], studentAttendance: [], followupEnquiry: [], frontdeskEnquiry: [], employeeAttendance: [] });
  const [loading, setLoading] = useState(false);
  const [selectedApiBranches, setSelectedApiBranches] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchData = async (branchIds) => {
    if (!branchIds || branchIds.length === 0 || !selectedSession || selectedSession === "0") return;
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        `${apiUrl}/DashboardTotalNumbers/Dashboard_AllModules?SessionId=${selectedSession}&BranchIds=${branchIds.join(",")}`,
        { method: "POST", headers: { "Content-Type": "application/json", Authorization: token } }
      );
      if (res.ok) {
        const d = await res.json();
        setLocalData({
          studentAdmission:  d.studentAdmission  || d.StudentAdmission  || [],
          loginDetail:       d.loginDetail       || d.LoginDetail       || [],
          studentAttendance: d.studentAttendance || d.StudentAttendance || [],
          followupEnquiry:   d.followupEnquiry   || d.FollowupEnquiry   || [],
          frontdeskEnquiry:  d.frontdeskEnquiry  || d.FrontdeskEnquiry  || [],
          employeeAttendance:d.employeeAttendance|| d.EmployeeAttendance|| [],
        });
      }
    } catch (e) { console.error("BranchWise fetch error:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (isMaster && branches.length > 0) {
      const ids = branches.map(b => b.branchId);
      setSelectedApiBranches(ids);
      fetchData(ids);
    } else if (!isMaster) {
      const id = sessionStorage.getItem("branchId");
      setSelectedApiBranches([id]);
      fetchData([id]);
    }
  }, [branches, isMaster, selectedSession]);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const allBranchNames = useMemo(() => {
    const s = new Set();
    Object.values(localData).forEach(arr => (arr || []).forEach(r => r.branchName && s.add(r.branchName.trim())));
    return [...s];
  }, [localData]);

  const colorFor = (name) => BRANCH_COLORS[allBranchNames.indexOf((name || "").trim()) % BRANCH_COLORS.length];

  const paginate = (rows) => {
    const total = Math.ceil(rows.length / BWS_PAGE_SIZE) || 1;
    const current = Math.min(page, total);
    return { rows: rows.slice((current - 1) * BWS_PAGE_SIZE, current * BWS_PAGE_SIZE), total, current };
  };

  const PaginationBar = ({ total, current }) => {
    if (total <= 1) return null;
    return (
      <div className="bws-pagination">
        <span className="bws-page-info">Page {current} of {total}</span>
        <div className="bws-page-btns">
          <button className="bws-page-btn" disabled={current <= 1} onClick={() => setPage(current - 1)}>‹ Prev</button>
          {Array.from({ length: total }, (_, i) => i + 1).map(p => (
            <button key={p} className={`bws-page-btn ${p === current ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="bws-page-btn" disabled={current >= total} onClick={() => setPage(current + 1)}>Next ›</button>
        </div>
      </div>
    );
  };

  const SimpleTable = ({ data, emptyMsg, search = "" }) => {
    // Filter rows by branch name search (admissions tab only)
    const filteredData = search.trim()
      ? (data || []).filter(r => (r.branchName || "").toLowerCase().includes(search.toLowerCase()))
      : (data || []);
    const { rows, total, current } = paginate(filteredData);
    const sum = (k) => filteredData.reduce((s, r) => s + (r[k] || 0), 0);
    return (
      <>
        <div className="bws-table-wrap">
          <table className="bws-table">
            <thead><tr>
              <th style={{ minWidth: 180 }}>Branch</th>
              <th className="bws-center">Total</th>
              <th className="bws-center">This Month</th>
              <th className="bws-center">This Week</th>
              <th className="bws-center">Today</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5}><div className="bws-empty"><span className="bws-empty-icon">⏳</span><span className="bws-empty-text">Loading…</span></div></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5}><div className="bws-empty"><span className="bws-empty-icon">🏫</span><span className="bws-empty-text">{emptyMsg || "No data"}</span></div></td></tr>
              ) : rows.map((r, i) => (
                <tr key={i}>
                  <td><span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="branch-dot" style={{ backgroundColor: colorFor(r.branchName) }} />
                    <strong style={{ color: "#0B1F40" }}>{r.branchName}</strong>
                  </span></td>
                  <td className="bws-center"><BwsPill value={r.total ?? r.Total} type="total" /></td>
                  <td className="bws-center"><BwsPill value={r.month ?? r.Month} type="month" /></td>
                  <td className="bws-center"><BwsPill value={r.week ?? r.Week} type="week" /></td>
                  <td className="bws-center"><BwsPill value={r.today ?? r.Today} type="today" /></td>
                </tr>
              ))}
            </tbody>
            {(data || []).length > 0 && !loading && (
              <tfoot><tr>
                <td>Σ Total ({(data || []).length} branches)</td>
                <td style={{ textAlign: "center" }}>{sum("total") || sum("Total")}</td>
                <td style={{ textAlign: "center" }}>{sum("month") || sum("Month")}</td>
                <td style={{ textAlign: "center" }}>{sum("week") || sum("Week")}</td>
                <td style={{ textAlign: "center" }}>{sum("today") || sum("Today")}</td>
              </tr></tfoot>
            )}
          </table>
        </div>
        <PaginationBar total={total} current={current} />
      </>
    );
  };

  const AttendanceTable = ({ data, emptyMsg }) => {
    const { rows, total, current } = paginate(data || []);
    return (
      <>
        <div className="bws-table-wrap">
          <table className="bws-table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ minWidth: 170, verticalAlign: "middle" }}>Branch</th>
                <th colSpan={3} className="bws-center" style={{ borderLeft: "2px solid rgba(56,189,248,0.25)" }}>Overall</th>
                <th colSpan={3} className="bws-center" style={{ borderLeft: "2px solid rgba(139,92,246,0.25)" }}>This Month</th>
                <th colSpan={2} className="bws-center" style={{ borderLeft: "2px solid rgba(244,185,66,0.25)" }}>Today</th>
              </tr>
              <tr>
                <th className="bws-center" style={{ borderLeft: "2px solid rgba(56,189,248,0.25)" }}>Present</th>
                <th className="bws-center">Absent</th>
                <th className="bws-center">Week Off</th>
                <th className="bws-center" style={{ borderLeft: "2px solid rgba(139,92,246,0.25)" }}>Present %</th>
                <th className="bws-center">Absent %</th>
                <th className="bws-center">Total</th>
                <th className="bws-center" style={{ borderLeft: "2px solid rgba(244,185,66,0.25)" }}>Present</th>
                <th className="bws-center">Absent</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}><div className="bws-empty"><span className="bws-empty-icon">⏳</span><span className="bws-empty-text">Loading…</span></div></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9}><div className="bws-empty"><span className="bws-empty-icon">📅</span><span className="bws-empty-text">{emptyMsg}</span></div></td></tr>
              ) : rows.map((r, i) => (
                <tr key={i}>
                  <td><span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="branch-dot" style={{ backgroundColor: colorFor(r.branchName) }} />
                    <strong style={{ color: "#0B1F40" }}>{r.branchName}</strong>
                  </span></td>
                  <td className="bws-center"><BwsPill value={r.totalPresent ?? r.TotalPresent} type="total" /></td>
                  <td className="bws-center"><BwsPill value={r.totalAbsent ?? r.TotalAbsent} type="total" /></td>
                  <td className="bws-center"><BwsPill value={r.totalWeekOff ?? r.TotalWeekOff} type="week" /></td>
                  <td className="bws-center" style={{ borderLeft: "2px solid rgba(139,92,246,0.1)" }}>
                    <BwsPctBar pct={r.monthPresentPercent ?? r.MonthPresentPercent ?? 0} color={
                      (r.monthPresentPercent ?? r.MonthPresentPercent ?? 0) >= 80 ? "#10B981"
                        : (r.monthPresentPercent ?? r.MonthPresentPercent ?? 0) >= 60 ? "#F4B942" : "#F43F5E"
                    } />
                  </td>
                  <td className="bws-center"><BwsPctBar pct={r.monthAbsentPercent ?? r.MonthAbsentPercent ?? 0} color="#F43F5E" /></td>
                  <td className="bws-center"><BwsPill value={r.monthTotal ?? r.MonthTotal} type="month" /></td>
                  <td className="bws-center" style={{ borderLeft: "2px solid rgba(244,185,66,0.1)" }}>
                    <BwsPill value={r.todayPresent ?? r.TodayPresent} type="today" />
                  </td>
                  <td className="bws-center"><BwsPill value={r.todayAbsent ?? r.TodayAbsent} type="today" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar total={total} current={current} />
      </>
    );
  };

  const selectedLabel = selectedApiBranches.length === 0 || selectedApiBranches.length === branches.length
    ? "All Branches"
    : `${selectedApiBranches.length} Branch${selectedApiBranches.length > 1 ? "es" : ""} Selected`;

  return (
    <div className="section-card" style={{ marginBottom: 28 }}>
      <div className="section-card-header">
        <div className="section-title-wrap">
          <div className="section-icon"><i className="bi bi-building" /></div>
          <h4 className="section-title">Branch-wise Detailed Statistics</h4>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {isMaster && (
            <div ref={dropdownRef} style={{ position: "relative" }}>
              <button
                onClick={() => setDropdownOpen(p => !p)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 14px", fontSize: 13, fontWeight: 600,
                  fontFamily: "Sora, sans-serif", background: "#fff",
                  border: "1.5px solid var(--glass-border)", borderRadius: "var(--radius-sm)",
                  cursor: "pointer", color: "var(--text-primary)", minWidth: 200,
                  boxShadow: dropdownOpen ? "0 0 0 3px rgba(56,189,248,0.15)" : "none",
                  borderColor: dropdownOpen ? "var(--accent-cyan)" : "var(--glass-border)",
                }}
              >
                <i className="bi bi-diagram-3" style={{ color: "#38BDF8" }} />
                {selectedLabel}
                <span style={{ marginLeft: "auto", color: "#38BDF8", fontSize: 11 }}>{dropdownOpen ? "▲" : "▼"}</span>
              </button>
              {dropdownOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1.5px solid var(--glass-border)", borderRadius: "var(--radius-md)", boxShadow: "0 8px 32px rgba(11,31,64,0.14)", zIndex: 100, minWidth: 240, padding: "10px 0", maxHeight: 320, overflowY: "auto" }}>
                  <div onClick={() => { const ids = branches.map(b => b.branchId); setSelectedApiBranches(ids); fetchData(ids); }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", fontSize: 13, fontWeight: selectedApiBranches.length === branches.length ? 700 : 500, color: selectedApiBranches.length === branches.length ? "#0284c7" : "var(--text-secondary)", background: selectedApiBranches.length === branches.length ? "rgba(56,189,248,0.08)" : "transparent" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, border: "2px solid", borderColor: selectedApiBranches.length === branches.length ? "#38BDF8" : "#cbd5e1", background: selectedApiBranches.length === branches.length ? "#38BDF8" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>
                      {selectedApiBranches.length === branches.length && "✓"}
                    </span>
                    All Branches
                  </div>
                  <div style={{ height: 1, background: "var(--glass-border)", margin: "6px 12px" }} />
                  {branches.map((branch) => {
                    const isChecked = selectedApiBranches.includes(branch.branchId);
                    const dotColor = BRANCH_COLORS[branches.indexOf(branch) % BRANCH_COLORS.length];
                    return (
                      <div key={branch.branchId}
                        onClick={() => setSelectedApiBranches(prev => isChecked ? prev.filter(id => id !== branch.branchId) : [...prev, branch.branchId])}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", fontSize: 13, fontWeight: isChecked ? 600 : 400, color: isChecked ? "#0284c7" : "var(--text-secondary)", background: isChecked ? "rgba(56,189,248,0.06)" : "transparent" }}>
                        <span style={{ width: 16, height: 16, borderRadius: 4, border: "2px solid", borderColor: isChecked ? "#38BDF8" : "#cbd5e1", background: isChecked ? "#38BDF8" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", flexShrink: 0 }}>{isChecked && "✓"}</span>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                        {branch.branchName}
                      </div>
                    );
                  })}
                  <div style={{ padding: "10px 12px 4px", borderTop: "1px solid var(--glass-border)", marginTop: 6 }}>
                    <button onClick={() => { fetchData(selectedApiBranches); setDropdownOpen(false); }}
                      style={{ width: "100%", padding: 8, fontSize: 13, fontWeight: 700, fontFamily: "Sora, sans-serif", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #38BDF8, #0ea5e9)", color: "#fff", cursor: "pointer" }}>
                      Apply Filter
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="bws-legend">
            {[["#38BDF8","Total"],["#8B5CF6","Month"],["#F4B942","Week"],["#10B981","Today"]].map(([c,l]) => (
              <div key={l} className="bws-legend-item"><span className="bws-legend-dot" style={{ background: c }} />{l}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="section-body">
        {/* ── Tab switcher + Search in one row ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
          <div className="bws-tabs" style={{ margin: 0 }}>
            {BWS_TABS.map(t => (
              <button key={t.key} className={`bws-tab ${activeTab === t.key ? "active" : ""}`}
                onClick={() => { setActiveTab(t.key); setPage(1); setAdmissionSearch(""); }}>{t.label}</button>
            ))}
          </div>
          {/* Search only visible on Admissions tab */}
          {activeTab === "studentAdmission" && (
            <input
              className="bws-search"
              placeholder="🔍  Search branch…"
              value={admissionSearch}
              onChange={e => { setAdmissionSearch(e.target.value); setPage(1); }}
            />
          )}
        </div>

        {/* ── Tables (search passed as prop to admissions only) ── */}
        {activeTab === "studentAdmission"  && <SimpleTable    data={localData.studentAdmission}  emptyMsg="No admission data" search={admissionSearch} />}
        {activeTab === "studentAttendance" && <AttendanceTable data={localData.studentAttendance} emptyMsg="No student attendance data" />}
        {activeTab === "employeeAttendance"&& <AttendanceTable data={localData.employeeAttendance} emptyMsg="No employee attendance data" />}
      </div>
    </div>
  );
}; 

/* ═══════════════════════════════════════════════════════
   ATTENDANCE CHARTS — uses Dashboard_AllModules data
═══════════════════════════════════════════════════════ */

/**
 * Build bar-chart data from the allModules response.
 * We show one bar-group per "period" selected (total / month / week / today)
 * with Student Present, Student Absent, Employee Present, Employee Absent side-by-side.
 */
const buildBarChartData = (allModulesData, period) => {
  const sa = allModulesData.studentAttendance  || [];
  const ea = allModulesData.employeeAttendance || [];

  // Collect all branch names
  const branchSet = new Set([
    ...sa.map(r => r.branchName || r.BranchName || ""),
    ...ea.map(r => r.branchName || r.BranchName || ""),
  ]);

  const prefix = period === "total" ? "total" : period; // total|month|week|today

  const getVal = (arr, branch, suffix) => {
    const row = arr.find(r => (r.branchName||r.BranchName||"") === branch);
    if (!row) return 0;
    // e.g. totalPresent / monthPresent / weekPresent / todayPresent
    const key1 = `${prefix}Present`;
    const key2 = `${prefix}Absent`;
    const key3 = `${prefix}WeekOff`;
    if (suffix === "present") return row[key1] ?? row[key1.charAt(0).toUpperCase()+key1.slice(1)] ?? 0;
    if (suffix === "absent")  return row[key2] ?? row[key2.charAt(0).toUpperCase()+key2.slice(1)] ?? 0;
    if (suffix === "weekoff") return row[key3] ?? row[key3.charAt(0).toUpperCase()+key3.slice(1)] ?? 0;
    return 0;
  };

  return [...branchSet].filter(Boolean).map(branch => ({
    branch: branch.length > 22 ? branch.slice(0, 20) + "…" : branch,
    fullBranch: branch,
    stuPresent: getVal(sa, branch, "present"),
    stuAbsent:  getVal(sa, branch, "absent"),
    empPresent: getVal(ea, branch, "present"),
    empAbsent:  getVal(ea, branch, "absent"),
  }));
};

/**
 * Build pie data from allModules for a given module & period
 */
const buildPieData = (allModulesData, moduleKey, period) => {
  const arr = allModulesData[moduleKey] || [];
  if (!arr.length) return { present: 0, absent: 0, weekOff: 0 };

  const prefix = period === "total" ? "total" : period;

  const sum = (suffix) => arr.reduce((s, r) => {
    const key = `${prefix}${suffix.charAt(0).toUpperCase()+suffix.slice(1)}`;
    return s + (r[key] ?? r[key.charAt(0).toUpperCase()+key.slice(1)] ?? 0);
  }, 0);

  return { present: sum("present"), absent: sum("absent"), weekOff: sum("weekOff") };
};

const AttendanceChartsSection = ({ allModulesData, onOpenModal, attendanceData, attendChartLoading, ddlClass, selectedClass, viewFrequency, onClassChange, onFrequencyChange, onBarClick }) => {  const [activePeriod, setActivePeriod] = useState("total");
  const [moduleView, setModuleView] = useState("both"); // "both" | "student" | "employee"

  const barData = useMemo(() => buildBarChartData(allModulesData, activePeriod), [allModulesData, activePeriod]);

const stuPie = useMemo(() => buildPieData(allModulesData, "studentAttendance",  activePeriod), [allModulesData, activePeriod]);
  const empPie = useMemo(() => buildPieData(allModulesData, "employeeAttendance", activePeriod), [allModulesData, activePeriod]);

  const hasBarData = barData.some(d => d.stuPresent+d.stuAbsent+d.empPresent+d.empAbsent > 0);

  // Present / Absent / Week-Off summary for the currently selected period + module view
  const summaryCards = useMemo(() => {
    const combined = moduleView === "student" ? stuPie
      : moduleView === "employee" ? empPie
      : { present: stuPie.present + empPie.present, absent: stuPie.absent + empPie.absent, weekOff: stuPie.weekOff + empPie.weekOff };
    return [
      { key: "present", label: "Present",  icon: "✅", value: combined.present, color: "#10B981" },
      { key: "absent",  label: "Absent",   icon: "❌", value: combined.absent,  color: "#F43F5E" },
      { key: "weekOff", label: "Week Off", icon: "🗓️", value: combined.weekOff, color: "#F4B942" },
    ];
  }, [moduleView, stuPie, empPie]);

  const PIE_COLORS = { present: "#38BDF8", absent: "#F43F5E", weekOff: "#F4B942" };

  const SinglePie = ({ title, data, icon, moduleType }) => {
    const total = data.present + data.absent + data.weekOff;
    const rate  = total > 0 ? ((data.present / total) * 100).toFixed(1) : "0.0";
    const pieData = [
      { name: "Present", value: data.present },
      { name: "Absent",  value: data.absent  },
      ...(data.weekOff > 0 ? [{ name: "Week Off", value: data.weekOff }] : []),
    ].filter(d => d.value > 0);

    const periodLabel = ATT_PERIODS.find(p => p.key === activePeriod)?.label || activePeriod;
    const filterMap   = { total: "Total", month: "Month", week: "Week", today: "Today" };

    return (
      <div className="single-pie-block">
        <div className="single-pie-title">
          <span style={{fontSize:20}}>{icon}</span>
          <span>{title}</span>
          <span style={{fontSize:11,color:"var(--text-muted)",fontWeight:500,marginLeft:4}}>({periodLabel})</span>
        </div>
        {total === 0 ? (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"24px 0",color:"var(--text-muted)",gap:8}}>
            <span style={{fontSize:32,opacity:0.3}}>📊</span>
            <span style={{fontSize:12}}>No data for this period</span>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <defs>
                  <linearGradient id={`pieG-${moduleType}-p`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#38BDF8" /><stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                  <linearGradient id={`pieG-${moduleType}-a`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#F43F5E" /><stop offset="100%" stopColor="#e11d48" />
                  </linearGradient>
                </defs>
                <Pie data={pieData} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" outerRadius={90} innerRadius={48} paddingAngle={3}
                  label={({name,percent})=>`${(percent*100).toFixed(0)}%`}
                  labelLine={{stroke:"rgba(15,42,85,0.2)"}}>
                  {pieData.map((entry,i) => (
                    <Cell key={i} fill={PIE_COLORS[entry.name==="Present"?"present":entry.name==="Absent"?"absent":"weekOff"]}
                      stroke="rgba(255,255,255,0.3)" strokeWidth={1} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{background:"#fff",border:"1.5px solid var(--glass-border)",borderRadius:10,fontFamily:"Sora,sans-serif",fontSize:12}} />
                <Legend wrapperStyle={{fontSize:11}} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-mini-stats">
              <div className="pie-mini-box" style={{borderColor:"rgba(56,189,248,0.25)",background:"rgba(56,189,248,0.04)"}}>
                <div className="pie-mini-val" style={{color:"#0284c7"}}>{data.present}</div>
                <div className="pie-mini-lbl">Present</div>
              </div>
              <div className="pie-mini-box" style={{borderColor:"rgba(244,63,94,0.25)",background:"rgba(244,63,94,0.04)"}}>
                <div className="pie-mini-val" style={{color:"#e11d48"}}>{data.absent}</div>
                <div className="pie-mini-lbl">Absent</div>
              </div>
              <div className="pie-mini-box" style={{borderColor:"rgba(16,185,129,0.25)",background:"rgba(16,185,129,0.04)"}}>
                <div className="pie-mini-val" style={{color:"#059669"}}>{rate}%</div>
                <div className="pie-mini-lbl">Rate</div>
              </div>
            </div>
            {/* <div style={{display:"flex",gap:8,width:"100%",marginTop:4}}>
              <button onClick={()=>onOpenModal("attendance", filterMap[activePeriod]||"Total",
                {title:`${periodLabel} — ${title} (Present)`, moduleType, attendanceType:"Present"})}
                style={{flex:1,padding:"8px",borderRadius:9,border:"1.5px solid rgba(16,185,129,0.3)",background:"rgba(16,185,129,0.06)",color:"#059669",fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:"Sora,sans-serif"}}>
                📗 Present
              </button>
              <button onClick={()=>onOpenModal("attendance", filterMap[activePeriod]||"Total",
                {title:`${periodLabel} — ${title} (Absent)`, moduleType, attendanceType:"Absent"})}
                style={{flex:1,padding:"8px",borderRadius:9,border:"1.5px solid rgba(244,63,94,0.3)",background:"rgba(244,63,94,0.06)",color:"#e11d48",fontWeight:700,fontSize:11,cursor:"pointer",fontFamily:"Sora,sans-serif"}}>
                📕 Absent
              </button>
            </div> */}
          </>
        )}
      </div>
    );
  };

  return (
    <>
     
     {/* ── Attendance Chart (day-wise) ── */}
<div className="section-card">
  <div className="section-card-header">
    <div className="section-title-wrap">
      <div className="section-icon"><i className="bi bi-bar-chart-fill" /></div>
      <h4 className="section-title">Attendance</h4>
    </div>
  </div>
  <div className="section-body">
    <div className="attend-controls">
      <div className="dash-select-wrap">
        <span className="dash-select-label">Class:</span>
        <select className="dash-select" value={selectedClass} onChange={onClassChange} style={{ minWidth: 160 }}>
          <option value="">All Classes</option>
          {ddlClass.map(item => (
            <option key={item.classId} value={item.classId}>{item.className}</option>
          ))}
        </select>
      </div>
      <div className="freq-controls">
        <span className="freq-label-text">View:</span>
        {["monthly", "weekly", "daily"].map(freq => (
          <div className="freq-btn" key={freq}>
            <input type="radio" id={`attend-${freq}`} value={freq}
              checked={viewFrequency === freq} onChange={onFrequencyChange} />
            <label htmlFor={`attend-${freq}`}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</label>
          </div>
        ))}
      </div>
    </div>

   {attendChartLoading ? (
  <div style={{ display: "flex", justifyContent: "center", padding: 56 }}>
    <div className="custom-spinner" />
  </div>
) : !attendanceData || attendanceData.length === 0 ? (
  <div className="chart-empty">
    <span className="chart-empty-icon">📊</span>
    <span className="chart-empty-text">No Record Found</span>
  </div>
) : (
  <div className="attend-scroll-wrap">
    <BarChart
      width={Math.max(attendanceData.length * 70, 600)}
      height={380}
      data={attendanceData}
      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
    >
      <defs>
        <linearGradient id="presentGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.9} />
          <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.6} />
        </linearGradient>
        <linearGradient id="absentGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.9} />
          <stop offset="95%" stopColor="#e11d48" stopOpacity={0.6} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,42,85,0.06)" />
      <XAxis dataKey="day" tick={{ fill: "#5a7ba8", fontSize: 11 }} axisLine={{ stroke: "rgba(15,42,85,0.1)" }} tickLine={false} />
      <YAxis tick={{ fill: "#5a7ba8", fontSize: 11 }} axisLine={{ stroke: "rgba(15,42,85,0.1)" }} tickLine={false} />
      <Tooltip content={<CustomBarTooltip />} cursor={{ fill: "rgba(56,189,248,0.06)" }} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <Bar
        dataKey="presentStudentsCount"
        fill="url(#presentGradient)"
        radius={[4,4,0,0]}
        name="Present"
        cursor="pointer"
        onClick={(data) => onBarClick(data, "Present")}
      />
      <Bar
        dataKey="absentStudentsCount"
        fill="url(#absentGradient)"
        radius={[4,4,0,0]}
        name="Absent"
        cursor="pointer"
        onClick={(data) => onBarClick(data, "Absent")}
      />
    </BarChart>
  </div>
)} 
  </div>
</div>

      {/* ── Dual Pie Snapshot ── */}
      <div className="section-card">
        <div className="section-card-header">
          <div className="section-title-wrap">
            <div className="section-icon"><i className="bi bi-pie-chart-fill" /></div>
            <div>
              <h4 className="section-title">Attendance Snapshot</h4>
              <p style={{margin:"2px 0 0",fontSize:11,color:"var(--text-muted)"}}>
                Student vs Employee breakdown · click bars above to switch period
              </p>
            </div>
          </div>
          <div className="att-toggle-group">
            {ATT_PERIODS.map(p => (
              <button key={p.key} className={`att-toggle-btn ${activePeriod===p.key?"active":""}`}
                onClick={()=>setActivePeriod(p.key)}>
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="section-body">
          <div className="dual-pie-row">
            <SinglePie title="Students"  icon="🎓" data={stuPie} moduleType="Student" />
            <SinglePie title="Employees" icon="👤" data={empPie} moduleType="Employee" />
          </div>
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════════════════ */
const Dashboard = () => {
  const navigate = useNavigate();

  const [isMaster, setIsMaster]             = useState(false);
  const [branches, setBranches]             = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [financialYears, setFinancialYears]  = useState([]);
  const [selectedSession, setSelectedSession] = useState("0");
  const [registrationData, setRegistrationData] = useState({});

  // All modules data — single source of truth for both tables AND charts
  const [allModulesData, setAllModulesData] = useState({
    studentAdmission:   [],
    loginDetail:        [],
    studentAttendance:  [],
    followupEnquiry:    [],
    frontdeskEnquiry:   [],
    employeeAttendance: [],
  });

  const [dashData, setDashData] = useState({
    totalAdmission: 0, totalTodayAdmission: 0, totalWeeklyAdmission: 0, totalMonthlyAdmission: 0,
  });

  const [dataLoading, setDataLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [attendanceData, setAttendanceData]       = useState([]);
const [attendChartLoading, setAttendChartLoading] = useState(false);
const [selectedClass, setSelectedClass]         = useState("");
const [viewFrequency, setViewFrequency]         = useState("monthly");
const [ddlClass, setDdlClass]                   = useState([]);

// Day-click detail modal
const [dayModalOpen, setDayModalOpen]       = useState(false);
const [dayModalLoading, setDayModalLoading] = useState(false);
const [dayModalTitle, setDayModalTitle]     = useState("");
const [dayModalRecords, setDayModalRecords] = useState([]);
  const [openSnackbar, setOpenSnackbar]     = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const showSnack = (msg, sev = "error") => { setSnackbarMessage(msg); setSnackbarSeverity(sev); setOpenSnackbar(true); };

  /* ── Modal helper ── */
  const openModal = (type, defaultFilter, extra = {}) => {
    const configs = {
      admissions: { type:"admissions", icon:"🎓", title:"Student Admissions", subtitle:"Detailed list of admitted students", defaultFilter },
      attendance: { type:"attendance", icon:"📅", title:extra.title||"Attendance Records", subtitle:"Attendance records", defaultFilter, ...extra },
    };
    setModalConfig(configs[type] || null);
  };

  const handleStatClick = (e, type, filter) => { e.stopPropagation(); openModal(type, filter); };

  /* ── API ── */
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem("token");
      const res = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method:"POST", headers:{"Content-Type":"application/json",Authorization:token}, body:JSON.stringify({})
      });
      if (res.ok) { const d = await res.json(); setBranches(d); }
    } catch(e) { console.error(e); }
  };

  const fetchFinancialYears = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem("token");
      const res    = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
        method:"POST", headers:{"Content-Type":"application/json",Authorization:token}, body:JSON.stringify({})
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const d    = await res.json();
      const list = d.data || d;
      setFinancialYears(list);
      const today = new Date();
      const cy = today.getFullYear();
      const inFY = today >= new Date(cy,3,1);
      const fyStr = inFY ? `${cy}-${cy+1}` : `${cy-1}-${cy}`;
      const cur = list.find(y => y.finanacialYear === fyStr);
      if (cur) { setSelectedSession(cur.financialYearID); setRegistrationData(p=>({...p,financialYearID:cur.financialYearID})); }
    } catch(e) { console.error(e); }
  };

  /**
   * Central fetch — Dashboard_AllModules
   * Used for KPI cards, branch-wise tables, AND all charts.
   */
  const fetchAllModules = async (sessionId, branchId) => {
    if (!sessionId || sessionId === "0" || !branchId) return;
    try {
      setDataLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem("token");
      const res    = await fetch(
        `${apiUrl}/DashboardTotalNumbers/Dashboard_AllModules?SessionId=${sessionId}&BranchIds=${branchId}`,
        { method:"POST", headers:{"Content-Type":"application/json",Authorization:token} }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const d = await res.json();

      const norm = {
        studentAdmission:   d.studentAdmission   || d.StudentAdmission   || [],
        loginDetail:        d.loginDetail         || d.LoginDetail         || [],
        studentAttendance:  d.studentAttendance   || d.StudentAttendance   || [],
        followupEnquiry:    d.followupEnquiry     || d.FollowupEnquiry     || [],
        frontdeskEnquiry:   d.frontdeskEnquiry    || d.FrontdeskEnquiry    || [],
        employeeAttendance: d.employeeAttendance  || d.EmployeeAttendance  || [],
      };
      setAllModulesData(norm);

      // KPI numbers (aggregate across all branches in response)
      const sumField = (arr, k1, k2) => (arr||[]).reduce((s,r) => s+(r[k1]??r[k2]??0), 0);
      const sa = norm.studentAdmission;
      setDashData({
        totalAdmission:       sumField(sa,"total",  "Total"),
        totalTodayAdmission:  sumField(sa,"today",  "Today"),
        totalWeeklyAdmission: sumField(sa,"week",   "Week"),
        totalMonthlyAdmission:sumField(sa,"month",  "Month"),
      });
    } catch(e) {
      console.error(e);
      showSnack("Failed to load dashboard data");
    } finally { setDataLoading(false); }
  };




const handleClassChange = (e) => setSelectedClass(e.target.value);
const handleFrequencyChange = (e) => setViewFrequency(e.target.value);

const fetchClass = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
    const response = await fetch(`${apiUrl}/Enquiry/ddlClassName?Branch_Id=${branchId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error(`Error fetching classes: ${response.status}`);
    const data = await response.json();
    if (data.data === null && data.msg === "Record Not Found") { setDdlClass([]); return; }
    setDdlClass(data);
  } catch (e) { console.error(e); setDdlClass([]); }
};

// Fetches day-wise present/absent counts directly from the grouped endpoints
const fetchAttendanceChartData = async (timeFrame) => {
  try {
    setAttendChartLoading(true);
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");

    const endpointMap = {
      daily:   "AttendanceRecord_Currentday",
      weekly:  "AttendanceRecord_CurrentWeek",
      monthly: "AttendanceRecord_CurrentMonth",
    };

    const res = await fetch(
      `${apiUrl}/DashboardTotalNumbers/${endpointMap[timeFrame]}?Branch_id=${branchId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId: selectedClass ? parseInt(selectedClass) : 0 }),
      }
    );

    if (res.ok) {
      const json = await res.json();
      // Currentday returns a plain array; Week/Month return { data: [...] , totalPresent, totalAbsent }
      const rows = Array.isArray(json) ? json : (json.data || []);
      setAttendanceData(rows);
    } else {
      setAttendanceData([]);
    }
  } catch (e) {
    console.error("Attendance chart fetch error:", e);
    setAttendanceData([]);
  } finally {
    setAttendChartLoading(false);
  }
};

// Called when a bar segment (Present/Absent) for a specific day is clicked
const handleDayBarClick = async (barPayload, status) => {
  if (!barPayload || !barPayload.day) return;
  setDayModalOpen(true);
  setDayModalLoading(true);
  setDayModalTitle(`${barPayload.day} — ${status}`);
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
    const filterTypeMap = { daily: "Today", weekly: "Week", monthly: "Month" };

    const res = await fetch(`${apiUrl}/DashboardTotalNumbers/GetAttendanceDetails`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token },
      body: JSON.stringify({
        moduleType: "Student",
        branchId: parseInt(branchId),
        sessionId: parseInt(selectedSession),
        type: status,               // "Present" | "Absent"
        filterType: filterTypeMap[viewFrequency] || "Month",
        pageNumber: 1,
        pageSize: 1000,
      }),
    });

    if (res.ok) {
      const json = await res.json();
      const rows = json.data || [];
      // Match rows whose date renders as the same "D MMM" label as the clicked bar
      const filtered = rows.filter(r => {
        const d = new Date(r.attendanceDate);
        const label = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }); // "7 Aug"
        return label === barPayload.day;
      });
      setDayModalRecords(filtered);
    } else {
      setDayModalRecords([]);
    }
  } catch (e) {
    console.error("Day detail fetch error:", e);
    setDayModalRecords([]);
  } finally {
    setDayModalLoading(false);
  }
};

  /* ── Effects ── */

 useEffect(() => {
  const master = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(master);
  fetchFinancialYears();
  if (master) {
    fetchBranches();
    // Auto-select the logged-in branch for master users too
    const bid = sessionStorage.getItem("branchId");
    if (bid) setSelectedBranchId(bid);
  } else {
    const bid = sessionStorage.getItem("branchId");
    setSelectedBranchId(bid);
  }
}, []);

useEffect(() => {
  if (selectedBranchId && selectedSession && selectedSession !== "0") {
    fetchAllModules(selectedSession, selectedBranchId);
    fetchClass();
  }
}, [selectedBranchId, selectedSession]);

// Re-fetch the day-wise chart whenever period, class, branch, or session changes
useEffect(() => {
  if (selectedBranchId && selectedSession && selectedSession !== "0") {
    fetchAttendanceChartData(viewFrequency);
  }
}, [selectedBranchId, selectedSession, viewFrequency, selectedClass]);

  const handleBranchChange  = (e) => setSelectedBranchId(e.target.value);
  const handleSessionChange = (e) => {
    const id = e.target.value;
    setSelectedSession(id);
    setRegistrationData(p => ({...p, financialYearID:id}));
  };

  return (
    <>
      <style>{dashboardStyles}</style>

      <div className="dash-root">
        <div className="dash-content">

          {/* ── Header ── */}
          <div className="dash-header">
            <div className="dash-title-wrap">
              <div className="dash-title-icon">
                <i className="bi bi-speedometer2" />
              </div>
              <div>
                <h3 className="dash-title-text">Dashboard</h3>
                <div className="dash-title-sub">Overview & Analytics</div>
              </div>
            </div>
            <div className="dash-header-controls">
              {isMaster && (
                <div className="dash-select-wrap">
                  <span className="dash-select-label">Branch:</span>
                  <select className="dash-select" value={selectedBranchId||""} onChange={handleBranchChange}>
                    <option value="">Select Branch</option>
                    {branches.map(b => <option key={b.branchId} value={b.branchId}>{b.branchName}</option>)}
                  </select>
                </div>
              )}
              <div className="dash-select-wrap">
                <span className="dash-select-label">Session:</span>
                <select className="dash-select" value={selectedSession} onChange={handleSessionChange}>
                  <option value="0">Select Session</option>
                  {financialYears.map(y => <option key={y.financialYearID} value={y.financialYearID}>{y.finanacialYear}</option>)}
                </select>
              </div>
            </div>
          </div>

          {dataLoading ? (
            <div className="dashboard-loading-container">
              <div className="custom-spinner" />
            </div>
          ) : (
            <>
              {/* ── KPI Cards ── */}
              <div className="stat-grid">
                <div className="stat-card stat-card-admissions"
                  onClick={() => navigate("/Admission/FrmAdmissionDetails.aspx/MQ==")}>
                  <div className="stat-card-header">
                    <span className="stat-card-title">Admissions</span>
                    <div className="stat-card-icon icon-cyan"><i className="mdi mdi-bookmark-outline" /></div>
                  </div>
                  <span className="stat-total-label">Total (Session)</span>
                  <div className="stat-total-value">{dashData.totalAdmission}</div>
                  <div className="stat-grid-bottom">
                    {[
                      {label:"Today", value:dashData.totalTodayAdmission,    filter:"Today"},
                      {label:"Week",  value:dashData.totalWeeklyAdmission,   filter:"Week"},
                      {label:"Month", value:dashData.totalMonthlyAdmission,  filter:"Month"},
                    ].map(({label,value,filter}) => (
                      <div key={label} className="stat-mini"
                        onClick={e => handleStatClick(e,"admissions",filter)}
                        title={`View ${label.toLowerCase()} admissions`}>
                        <span className="stat-mini-label">{label}</span>
                        <span className="stat-mini-value">{value}</span>
                        {/* <span className="stat-mini-hint">View →</span> */}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Branch-wise Section — passes allModulesData directly (no separate fetch) ── */}
              <BranchWiseSection
                branches={branches}
                selectedSession={selectedSession}
                isMaster={isMaster}
                allModulesData={allModulesData}
                loading={dataLoading}
              />

              {/* ── Attendance Charts (Bar + Dual Pie) ── */}
  <AttendanceChartsSection
  allModulesData={allModulesData}
  onOpenModal={openModal}
  attendanceData={attendanceData}
  attendChartLoading={attendChartLoading}
  ddlClass={ddlClass}
  selectedClass={selectedClass}
  viewFrequency={viewFrequency}
  onClassChange={handleClassChange}
  onFrequencyChange={handleFrequencyChange}
  onBarClick={handleDayBarClick}
/>
            </>
          )}
        </div>
      </div>

      {/* ── Day Attendance Click Modal ── */}
<DayAttendanceModal
  open={dayModalOpen}
  loading={dayModalLoading}
  title={dayModalTitle}
  records={dayModalRecords}
  onClose={() => setDayModalOpen(false)}
/>

{modalConfig && (
  <DetailModal
    config={modalConfig}
    onClose={() => setModalConfig(null)}
    selectedSession={selectedSession}
    branchId={isMaster ? selectedBranchId : sessionStorage.getItem("branchId")}
  />
)}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={()=>setOpenSnackbar(false)}
        anchorOrigin={{vertical:"top",horizontal:"center"}}>
        <Alert onClose={()=>setOpenSnackbar(false)} severity={snackbarSeverity} sx={{width:"100%"}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Dashboard;